import { useEffect } from 'react';

/**
 * GalleryLightbox
 * Full-screen image viewer with keyboard navigation.
 *
 * Props:
 *   images   — array of { src, title }
 *   index    — currently open image index (null = closed)
 *   onClose  — called when lightbox should close
 *   onPrev   — called to go to previous image
 *   onNext   — called to go to next image
 */
const GalleryLightbox = ({ images, index, onClose, onPrev, onNext }) => {
    useEffect(() => {
        const onKey = (e) => {
            if (e.key === 'Escape')     onClose();
            if (e.key === 'ArrowLeft')  onPrev();
            if (e.key === 'ArrowRight') onNext();
        };
        window.addEventListener('keydown', onKey);
        return () => window.removeEventListener('keydown', onKey);
    }, [onClose, onPrev, onNext]);

    if (index === null || index === undefined) return null;

    return (
        <div
            className="fixed inset-0 z-[9999] bg-black/90 flex items-center justify-center p-4"
            onClick={onClose}
        >
            {/* Close */}
            <button
                className="absolute top-4 right-4 text-white/60 hover:text-white transition-colors p-2"
                onClick={onClose}
            >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M6 18L18 6M6 6l12 12" />
                </svg>
            </button>

            {/* Prev */}
            <button
                className="absolute left-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-2"
                onClick={(e) => { e.stopPropagation(); onPrev(); }}
            >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M15 19l-7-7 7-7" />
                </svg>
            </button>

            {/* Image */}
            <img
                src={images[index]?.src}
                alt={images[index]?.title || ''}
                className="max-h-[90vh] max-w-[90vw] object-contain rounded-lg"
                onClick={(e) => e.stopPropagation()}
            />

            {/* Next */}
            <button
                className="absolute right-4 top-1/2 -translate-y-1/2 text-white/60 hover:text-white transition-colors p-2"
                onClick={(e) => { e.stopPropagation(); onNext(); }}
            >
                <svg className="w-8 h-8" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                </svg>
            </button>

            {/* Counter */}
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white/40 text-xs font-medium">
                {index + 1} / {images.length}
            </div>
        </div>
    );
};

export default GalleryLightbox;
